import React from "react";
import SellerReducer from "./reducer";
import { SellerDispatch, SellerProviderProps, SellerState } from "./types";

const SellerStateContext = React.createContext<SellerState | undefined>(undefined);
const SellerDispatchContext = React.createContext<SellerDispatch | undefined>(undefined);

const initialState: SellerState = { currentSeller: null, authorizedSellerIds: [] };

const SellerProvider: React.FC<SellerProviderProps> = ({ children }) => {
  const [state, dispatch] = React.useReducer(SellerReducer, initialState);
  return (
    <SellerStateContext.Provider value={state}>
      <SellerDispatchContext.Provider value={dispatch}>{children}</SellerDispatchContext.Provider>
    </SellerStateContext.Provider>
  );
};

const useSellerState = (): SellerState => {
  const context = React.useContext(SellerStateContext);
  if (context === undefined) {
    throw new Error("useSellerState must be used within a SellerProvider");
  }
  return context;
};

const useSellerDispatch = (): SellerDispatch => {
  const context = React.useContext(SellerDispatchContext);
  if (context === undefined) {
    throw new Error("useSellerDispatch must be used within a SellerProvider");
  }
  return context;
};

/**
 * Như `useSellerState` nhưng trả `undefined` thay vì ném khi ở ngoài `SellerProvider`.
 *
 * Dành cho component KHÔNG được phép chết vì thiếu provider — cụ thể là những component mount ở mọi
 * màn (SupportLauncher) và những cây render ngoài app thật (preview dev, test). Ở đó seller chỉ là
 * thông tin làm giàu: thiếu thì bớt một khối, không phải hỏng cả màn.
 *
 * Không thay `useSellerState`: chỗ nào seller là BẮT BUỘC thì ném vẫn đúng — im lặng ở đó sẽ thành
 * một màn thiếu dữ liệu mà không ai biết vì sao.
 */
const useSellerStateOptional = (): SellerState | undefined => React.useContext(SellerStateContext);

export * from "./actions";
export { SellerProvider, useSellerState, useSellerStateOptional, useSellerDispatch };
